import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweepOutlined";
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import DeleteIconButton from "_shared/_views/buttons/delete-icon-button";
import AssistantConversationSummary from "ai-assistant/conversations/assistant-conversation-summary";
import React from "react";

export interface AssistantConversationListProps {
  /** Undefined while the list is loading. */
  conversations: AssistantConversationSummary[] | undefined;
  /** Whether the list could not be loaded. */
  failedToLoad: boolean;
  /** The conversation the panel has open, marked as current in the list. */
  openConversationId: string | undefined;
  /** What the last open or delete said, when it did not simply work. */
  notice: string | undefined;
  /** Disables every control, while an open or a delete is under way. */
  busy: boolean;
  onOpen: (conversation: AssistantConversationSummary) => void;
  onDelete: (conversation: AssistantConversationSummary) => void;
  onDeleteAll: () => void;
}

/**
 * The user's earlier assistant conversations, most recently used first, as the server orders them.
 *
 * Each entry shows what the user first asked and nothing the assistant wrote. The list is read
 * without reauthorizing any citation, so the server sends only the user's own words; an answer,
 * or a title generated from one, could carry document content the user may no longer read. A
 * conversation's answers are shown only once it is opened, which re-checks every citation.
 */
export default function AssistantConversationList({
  conversations,
  failedToLoad,
  openConversationId,
  notice,
  busy,
  onOpen,
  onDelete,
  onDeleteAll,
}: AssistantConversationListProps) {
  const headingId = React.useId();

  return (
    <ListContainer aria-labelledby={headingId}>
      <ListHeader>
        <ListHeading id={headingId}>Your conversations</ListHeading>
        {conversations !== undefined && conversations.length > 0 && (
          <DeleteAllButton
            aria-label="Delete your assistant conversations"
            color="error"
            disabled={busy}
            size="small"
            endIcon={<DeleteSweepIcon />}
            onClick={onDeleteAll}
          >
            Delete all
          </DeleteAllButton>
        )}
      </ListHeader>
      {notice !== undefined && <ListNotice role="status">{notice}</ListNotice>}
      {failedToLoad ? (
        <ListMessage role="alert">Your conversations could not be loaded. Please try again.</ListMessage>
      ) : conversations === undefined ? (
        <ListMessage>Loading your conversations…</ListMessage>
      ) : conversations.length === 0 ? (
        <ListMessage>You have no earlier conversations.</ListMessage>
      ) : (
        <Entries>
          {conversations.map(conversation => {
            const isOpen = conversation.conversationId === openConversationId;
            return (
              <Entry key={conversation.conversationId}>
                <EntryButton
                  type="button"
                  disabled={busy}
                  aria-current={isOpen ? "true" : undefined}
                  onClick={() => onOpen(conversation)}
                >
                  <Opening>{conversation.opening}</Opening>
                  <Details>
                    {isOpen && <OpenTag>Open now · </OpenTag>}
                    {conversation.lastMessageAt.format("MMM D, h:mm A")}
                    {" · "}
                    {questionCount(conversation.turnCount)}
                  </Details>
                </EntryButton>
                <Tooltip title="Delete this conversation">
                  <span>
                    <DeleteIconButton
                      aria-label={`Delete the conversation "${conversation.opening}"`}
                      disabled={busy}
                      size="small"
                      onClick={() => onDelete(conversation)}
                    >
                      <DeleteIcon fontSize="small" />
                    </DeleteIconButton>
                  </span>
                </Tooltip>
              </Entry>
            );
          })}
        </Entries>
      )}
    </ListContainer>
  );
}

function questionCount(turns: number): string {
  return turns === 1 ? "1 question" : `${turns} questions`;
}

// #region Styled Components
const ListContainer = styled("section")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  minHeight: 0,
}));
const ListHeader = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  justifyContent: "space-between",
  gap: theme.spacing(1),
}));
const ListHeading = styled("h4")(({ theme }) => ({
  ...theme.typography.subtitle1,
  fontWeight: 500,
  margin: 0,
}));
const ListMessage = styled("p")(({ theme }) => ({
  color: theme.palette.text.secondary,
  margin: 0,
}));
const ListNotice = styled("p")(({ theme }) => ({
  backgroundColor: theme.palette.action.hover,
  borderRadius: theme.shape.borderRadius,
  margin: 0,
  padding: theme.spacing(1, 1.5),
}));
const Entries = styled("ul")({
  listStyle: "none",
  margin: 0,
  padding: 0,
});
const Entry = styled("li")(({ theme }) => ({
  alignItems: "center",
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: "flex",
  gap: theme.spacing(0.5),
}));
const EntryButton = styled("button")(({ theme }) => ({
  ...theme.typography.body2,
  background: "none",
  border: "none",
  borderRadius: theme.shape.borderRadius,
  color: "inherit",
  cursor: "pointer",
  display: "flex",
  flex: 1,
  flexDirection: "column",
  gap: theme.spacing(0.25),
  minWidth: 0,
  padding: theme.spacing(1),
  textAlign: "left",
  "&:hover:not(:disabled)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:focus-visible": {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: -2,
  },
  "&:disabled": {
    color: theme.palette.text.disabled,
    cursor: "default",
  },
  "&[aria-current='true']": {
    backgroundColor: theme.palette.action.selected,
  },
}));
const Opening = styled("span")({
  display: "-webkit-box",
  overflow: "hidden",
  overflowWrap: "anywhere",
  WebkitBoxOrient: "vertical",
  WebkitLineClamp: 2,
});
const Details = styled("span")(({ theme }) => ({
  ...theme.typography.caption,
  color: theme.palette.text.secondary,
}));
const OpenTag = styled("span")(({ theme }) => ({
  color: theme.palette.primary.main,
  fontWeight: 500,
}));
const DeleteAllButton = styled(Button)({
  flexShrink: 0,
  paddingRight: 5,
});
// #endregion Styled Components
